// This code is from a custom script that Sprii made for us.
// It replaces one of the handlers in their main script so that we can store the orderNo.

import { IActionContext, IRequestContext } from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import { Cart } from '@msdyn365-commerce/retail-proxy';
import { SpriiService } from '../../../integrations/sprii/sprii-service';
import { CookieService } from '../../../shared/services/cookie-service/cookie-service';
import { CookieNames } from '../../../shared/services/cookie-service/cookie-service.const';
import { SpriiProduct } from '../cus-sprii-integration.types';

const getApiSettings = (request: IRequestContext) => {
    const apiSettings = request.apiSettings;
    if (!apiSettings?.baseUrl || !apiSettings?.oun) {
        return null;
    }
    const baseUrl = apiSettings.baseUrl.replace(/\/$/, '');
    return { baseUrl, oun: apiSettings.oun };
};

const generateRequestId = () => {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes)
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('');
};

const getUserSessionIdFromCookie = (cookieService: CookieService) => {
    const cookieValue = cookieService.getRequestCookie(CookieNames.ApplicationInsightsUser);
    if (!cookieValue) {
        return null;
    }
    const [userSessionId] = cookieValue.split('|');
    return userSessionId || null;
};

const getPostHeaders = (oun: string, requestid: string, userSessionId: string | null) => ({
    accept: 'application/json',
    'accept-language': 'en-US',
    'content-type': 'application/json',
    contenttype: 'application/json',
    'from-keystone': 'true',
    'odata-maxversion': '4.0',
    'odata-version': '4.0',
    oun,
    prefer: 'return=representation',
    requestid,
    ...(userSessionId ? { usersessionid: userSessionId } : {})
});

const addProductToCart = async (product: SpriiProduct, actionContext: IActionContext, cookieService: CookieService) => {
    const recordId = product.custom_attributes?.['record id'];
    const ProductId = typeof recordId === 'number' || typeof recordId === 'string' ? Number(recordId) : NaN;
    if (!Number.isSafeInteger(ProductId) || ProductId <= 0) {
        throw new Error('Invalid Sprii product record id');
    }

    const cartState = await getCartState(actionContext);
    const cartId = cartState.cart.Id;
    if (!cartId) {
        throw new Error('Could not initialize Dynamics 365 cart');
    }

    const quantity = product.quantity;
    const apiSettings = getApiSettings(actionContext.requestContext);
    if (!apiSettings) {
        throw new Error('Could not resolve Dynamics 365 API settings from page');
    }
    const { baseUrl, oun } = apiSettings;
    const requestId = generateRequestId();
    const userSessionId = getUserSessionIdFromCookie(cookieService);
    const cartStatusRes = await fetch(`${baseUrl}/Commerce/Carts('${cartId}')?api-version=7.3`, {
        method: 'GET',
        headers: {
            oun,
            requestid: `${requestId}/1`
        }
    });
    if (!cartStatusRes.ok) {
        throw new Error(`Dynamics 365 cart GET failed: HTTP ${cartStatusRes.status}`);
    }
    const cartStatus: Cart = await cartStatusRes.json();
    const version = cartStatus?.Version;
    const oldCartLines = cartStatus?.CartLines;
    const productExisting = oldCartLines?.find(line => line.ProductId === ProductId);
    if (productExisting?.Quantity === quantity) {
        return;
    }
    if (productExisting) {
        const updateCartLinesResponse = await fetch(`${baseUrl}/Commerce/Carts('${cartId}')/UpdateCartLines?api-version=7.3`, {
            headers: getPostHeaders(oun, `${requestId}/2`, userSessionId),
            referrer: `${window.location.origin}/`,
            referrerPolicy: 'strict-origin-when-cross-origin',
            body: JSON.stringify({
                cartLines: [
                    {
                        ...productExisting,
                        Quantity: quantity
                    }
                ]
            }),
            method: 'POST',
            mode: 'cors',
            credentials: 'omit'
        });
        if (!updateCartLinesResponse.ok) {
            throw new Error(`Dynamics 365 UpdateCartLines failed: HTTP ${updateCartLinesResponse.status}`);
        }
        return;
    }
    const addCartLinesResponse = await fetch(`${baseUrl}/Commerce/Carts('${cartId}')/AddCartLines?api-version=7.3`, {
        headers: getPostHeaders(oun, `${requestId}/2`, userSessionId),
        referrer: `${window.location.origin}/`,
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
            cartLines: [
                {
                    ProductId,
                    Quantity: quantity
                }
            ],
            cartVersion: version
        }),
        method: 'POST',
        mode: 'cors',
        credentials: 'omit'
    });
    if (!addCartLinesResponse.ok) {
        const responseBody = await addCartLinesResponse.text();
        throw new Error(`Dynamics 365 AddCartLines failed: HTTP ${addCartLinesResponse.status}. ${responseBody ? `${responseBody}` : ''}`);
    }
};

export const registerSpriiCheckoutHandler = (actionContext: IActionContext) => {
    if (typeof window === 'undefined') {
        return;
    }

    const request = actionContext.requestContext;
    const spriiService = new SpriiService(request);
    const cookieService = new CookieService(request);

    (window.onSpriiReady = window.onSpriiReady || []).push({
        priority: 100,
        handler: sdk => {
            sdk.registerAction(sdk.actions.SOME_CHECKOUT, async payload => {
                actionContext.telemetry.error(`[Sprii checkout handler] Running checkout handler for orderNo: ${payload.orderNo}`);
                try {
                    if (payload.orderNo) {
                        await spriiService.setSpriiOrderNo(payload.orderNo);
                    }

                    for (const product of payload.products) {
                        await addProductToCart(product, actionContext, cookieService);
                    }
                    return { status: sdk.actionStatuses.SUCCESS };
                } catch (error) {
                    // Sprii ignores the message of the error we throw, so we also log it ourselves
                    console.error(error);
                    actionContext.telemetry.error(`[Sprii checkout handler] checkout action failed: ${String(error)}`);
                    throw error;
                }
            });
        }
    });
};
